import {css, keyframes} from '@emotion/react';
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {LanguageSwitch} from 'UI/components/action/LanguageSwitch';

import {useLanguagePopup} from './useLanguagePopup';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const scaleIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
`;

/**
 * The props for the `LanguagePopup` component.
 * Configures the initial language selection overlay shown on first visit.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'LanguagePopup'
     */
    testId?: string;
}

/**
 * Renders a full-screen overlay popup for initial language selection.
 * Shows on first visit (when no language preference is stored in localStorage)
 * and pre-selects the language matching the browser's locale.
 *
 * @param props        The component props.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'LanguagePopup'`.
 * @returns A JSX element rendering the LanguagePopup, or null when closed.
 *
 * @example
 * ```tsx
 * <LanguagePopup />
 * ```
 */
const LanguagePopup = ({testId = 'LanguagePopup'}: ComponentProps) => {
    const {handleConfirm, isOpen, t} = useLanguagePopup();

    return isOpen
        ? (
            <Backdrop data-cy={testId}>
                <Dialog>
                    <Title>{t('languagePopup.title')}</Title>
                    <Subtitle>{t('languagePopup.subtitle')}</Subtitle>
                    <LanguageSwitch justify="center" testId={`${testId}-switch`} />
                    <ConfirmButton
                        data-cy={`${testId}-confirm`}
                        type="button"
                        onClick={handleConfirm}
                    >
                        {t('languagePopup.confirm')}
                    </ConfirmButton>
                </Dialog>
            </Backdrop>
        )
        : null;
};

LanguagePopup.displayName = 'LanguagePopup';

export {LanguagePopup};

// ── Styled ──────────────────────────────────────────────────

const Backdrop = styled.div`
    align-items: center;
    animation: ${css`${fadeIn}`} 0.25s ease forwards;
    background: rgba(0, 0, 0, 0.5);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 10000;
`;

const Dialog = styled.div`
    animation: ${css`${scaleIn}`} 0.2s ease forwards;
    background: ${({theme}) => theme.colors.white};
    border-radius: ${spacing(4)};
    box-shadow: ${({theme}) => theme.boxShadows.floatingShadow};
    display: flex;
    flex-direction: column;
    gap: ${spacing(5)};
    margin: ${spacing(4)};
    max-width: 360px;
    padding: ${spacing(8)};
    text-align: center;
    width: 100%;
`;

const Title = styled.h2`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-family: ${({theme}) => theme.fonts.NotoSerif};
    font-size: 2.2rem;
    font-weight: 700;
    margin: 0;
`;

const Subtitle = styled.p`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    margin: 0;
`;

const ConfirmButton = styled.button`
    background: ${({theme}) => theme.colors.primaryDefault};
    border: none;
    border-radius: ${spacing(6)};
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    font-weight: 600;
    padding: ${spacing(3)} ${spacing(6)};
    transition: background 0.2s ease;
    width: 100%;

    &:hover {
        background: ${({theme}) => theme.colors.primaryLight};
    }
`;